import { useEffect, useState, useRef, useCallback } from 'react';
import { getSocket } from '../lib/socket';
import type { Socket } from 'socket.io-client';

/**
 * Subscribe to a bot's WebSocket room and listen for events.
 * Returns a stable `on` function to register event listeners.
 */
export function useBotSocket(botId: string | undefined) {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!botId) return;

    const socket = getSocket();
    socketRef.current = socket;

    const onConnect = () => {
      setConnected(true);
      socket.emit('subscribe:bot', botId);
    };

    const onDisconnect = () => setConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    if (socket.connected) {
      setConnected(true);
      socket.emit('subscribe:bot', botId);
    }

    return () => {
      socket.emit('unsubscribe:bot', botId);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [botId]);

  const on = useCallback(
    (event: string, handler: (...args: any[]) => void) => {
      const socket = socketRef.current;
      if (!socket) return () => {};
      socket.on(event, handler);
      return () => { socket.off(event, handler); };
    },
    [],
  );

  return { connected, on };
}

/**
 * Subscribe to user-level events (alerts).
 */
export function useAlertSocket(onAlert?: (data: any) => void) {
  useEffect(() => {
    if (!onAlert) return;

    const socket = getSocket();
    socket.on('alert:new', onAlert);
    return () => { socket.off('alert:new', onAlert); };
  }, [onAlert]);
}

/**
 * Track WebSocket connection status.
 */
export function useSocketStatus() {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const socket = getSocket();
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    setConnected(socket.connected);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, []);

  return connected;
}
